import { Injectable } from "@angular/core";
import { NetworkConnectionType, NetworkService } from "../network.service";
import { OfferInfo } from "~/app/core/model/offer-info";
import { OfferSearch } from "~/app/core/model/offer-search";
import { LocationService } from "~/app/core/location.service";
import { NotificationService } from "~/app/core/notification.service";
import { SettingsService } from "~/app/core/settings.service";
import { StorageService } from "~/app/core/storage.service";
import { HttpOfferService } from "~/app/core/offer-service/http-offer-service";
import { SampleOfferService } from "~/app/core/offer-service/sample-offer-service";
import { InterfaceOfferService } from "~/app/core/offer-service/interface-offer-service";
import { SuccessResult } from "~/app/core/model/connect";
import { UserProfileService } from "~/app/core/user-profile-service/user-profile.service";
import { Settings } from "~/app/core/model/settings";
import * as Log from "~/app/core/log";
import { RPCService } from "~/app/core/rpc.service";
import { toDate } from "~/app/core/model/date-str";
import { AlternateLocationsList } from "~/app/core/model/rpc/alternate-locations-list";
import { SponsorInfo } from "~/app/core/model/sponsor-info";
import { OfferAvailableInfo } from "~/app/core/model/rpc/offer-available-info";
import { OfferItemView } from "~/app/components/offer-item/offer-item-view";
import { OfferFilter } from "~/app/core/model/offer-filter";
import { OfferFilterOptions } from "~/app/core/model/offer-filter-options";
import { LocationInfo } from "~/app/core/model/location-info";
import { TicketService } from "~/app/core/model/ticket-service/ticket.service";
import {ToastService} from "~/app/core/toast.service";

@Injectable()
export class OfferService {
    private readonly httpService: HttpOfferService;
    private readonly sampleService: SampleOfferService;

    private sample: boolean = null;
    private service: InterfaceOfferService;

    private get settings(): Settings {
        return this.settingsService.loadSettings();
    }

    constructor(
        private readonly networkService: NetworkService,
        private readonly userProfile: UserProfileService,
        private readonly ticketService: TicketService,
        private readonly locationService: LocationService,
        private readonly toastService: ToastService,
        private readonly notificationService: NotificationService,
        private readonly settingsService: SettingsService,
        private readonly storageService: StorageService,
        private readonly rpcService: RPCService
    ) {
        Log.debug(Log.Tag.OfferService, "CONSTRUCT OFFERS SERVICE");
        this.sampleService = new SampleOfferService(locationService, notificationService, settingsService, storageService);
        this.httpService = new HttpOfferService(networkService, userProfile, locationService, notificationService, settingsService, storageService, rpcService);
        this.setUseSample(this.settingsService.loadSettings().useSampleOffers);
    }

    setUseSample(useSamples: boolean) {
        const changed = this.sample !== useSamples;
        if (changed) {
            const search = this.storageService.loadOfferSearch();
            const settings: Settings = this.settingsService.loadSettings();
            settings.useSampleOffers = useSamples;
            this.settingsService.saveSettings(settings);
            if (this.sample != null) {
                this.storageService.resetData();
                this.storageService.resetLogin();
                if (search) {
                    this.storageService.storeOfferSearch(search);
                }
            }

            if (useSamples) {
                Log.debug(Log.Tag.OfferService, "Setup sample offers service.");
                this.service = this.sampleService;
            } else {
                Log.debug(Log.Tag.OfferService, "Setup http offers service.");
                this.service = this.httpService;
            }

            this.service.initService();
            this.sample = useSamples;
        }
    }

    getOffer(offerId: number): OfferInfo {
        return this.storageService.loadOfferInfo(offerId);
    }

    async getOfferViaApi(offerId: number): Promise<OfferInfo> {
      return this.httpService.retrieveOffer(offerId).then(value => {
        return HttpOfferService.convertOfferInfoRPC(value.offers.pop(), this.settings);
      });
    }

    generateOfferShare(offerId: number): Promise<{ url: string, image: string }> {
        return this.service.generateOfferShare(offerId);
    }

    nearbyOffer(offerId: number, addressId: number) {
        this.service.nearbyOffer(offerId, addressId);
    }

    seenOffer(offerId: number) {
        this.service.seenOffer(offerId);
    }

    watchedVideo(offerId: number): OfferInfo {
        return this.service.watchedVideo(offerId);
    }

    revealOffer(offerId: number): Promise<OfferInfo> {
        const offer = this.getOffer(offerId);
        if (!offer) return Promise.reject();
        // abort the reveal process
        if (offer.revealed) return Promise.resolve(offer);
        if (offer.revealCost > this.ticketService.availableTicketCount) return Promise.reject();
        return new Promise((resolve, reject) => {
            this.service.revealOffer(offerId).then((info) => {
                this.ticketService.spendTickets(offer.revealCost, "Unlocked this offer!");
                resolve(info);
            }, (error) => reject(error));
        });
    }

    repeatUnlockOffer(offerId: number): Promise<OfferInfo> {
        const offer = this.getOffer(offerId);
        if (!offer) return Promise.reject();
        // abort the reveal process
        if (offer.repeatUnlocked) return Promise.resolve(offer);
        if (offer.repeatUnlockCost > this.ticketService.availableTicketCount) return Promise.reject();
        return new Promise((resolve, reject) => {
            this.service.repeatUnlockOffer(offerId).then((info) => {
                this.ticketService.spendTickets(offer.repeatUnlockCost, "Unlocked this offer for re-use!");
                resolve(info);
            }, (error) => reject(error));
        });
    }

    favoriteOffer(offerId: number): Promise<OfferInfo> {
        const offer = this.getOffer(offerId);
        if (!offer) return Promise.reject();
        // abort the reveal process
        if (offer.favorite) return Promise.resolve(offer);
        if (offer.favoriteCost > this.ticketService.availableTicketCount) return Promise.reject();
        return new Promise((resolve, reject) => {
            this.service.favoriteOffer(offerId).then((info) => {
                this.ticketService.favoritedOffer().then(() => {
                    this.ticketService.spendTickets(offer.favoriteCost, "Moved to Favorites", 2500);
                });
                resolve(info);
            }, (error) => reject(error));
        });
    }

    unfavoriteOffer(offerId: number): Promise<OfferInfo> {
        const offer = this.getOffer(offerId);
        if (!offer) return Promise.reject();
        // abort the reveal process
        if (!offer.favorite) return Promise.resolve(offer);
        return new Promise((resolve, reject) => {
            this.service.unfavoriteOffer(offerId).then((info) => {
                this.toastService.toastSuccess({
                    header: "Unfavorited Offer",
                    duration: 1500,
                    message: "Find it again in the Discovery tab.",
                    // onHide: () => resolve(info)
                });
                resolve(info);
            }, (error) => reject(error));
        });
    }

    redeemOffer(offerId: number, addressId: number): Promise<OfferInfo> {
        return new Promise((resolve, reject) => {
            this.service.redeemOffer(offerId, addressId).then((offerInfo) => {
                // add id to redeemed offer list
                this.storageService.addOffersListRedeemedOffer(offerInfo.id);
                // schedule follow up notification
                // this.notificationService.scheduleBouncebackNotification(offerInfo);
                resolve(offerInfo);
            }, (error) => {
                reject(error);
            });
        });
    }

    isAvailableOffer(offerId: number): Promise<OfferAvailableInfo> {
        return this.service.isAvailableOffer(offerId);
    }

    testResetOffers(): Promise<SuccessResult> {
        return this.service.testResetOffers();
    }

    testResetOffer(offerId: number, resetRedeemed: boolean, resetRevealed: boolean, resetRepeatUnlocked: boolean, resetThumbsup: boolean, resetBounceback: boolean, resetVideo: boolean): Promise<OfferInfo> {
        return new Promise((resolve, reject) => {
            this.service.testResetOffer(offerId, resetRedeemed, resetRevealed, resetRepeatUnlocked, resetThumbsup, resetBounceback, resetVideo).then((offerInfo) => {
                if (!offerInfo.redeemedDate) this.storageService.removeOffersListRedeemedOffer(offerId);
                // this.notificationService.clearNotification(offerInfo.id);
                // this.notificationService.scheduleBouncebackNotification(offerInfo);
                resolve(offerInfo);
            }, (error) => {
                reject(error);
            });
        });
    }

    getSearch(): OfferSearch {
        return this.storageService.loadOfferSearch();
    }

    setSearch(search: OfferSearch) {
        this.storageService.storeOfferSearch(search);
        this.storageService.storeOfferListSearch(null);
        this.storageService.storeOfferSearchDate(null);
    }

    searchExpired(): boolean {
        const nextSearchExpire: Date = this.storageService.loadOfferSearchExpiresDate();
        const lastSearch: Date = this.storageService.loadOfferSearchDate();
        if (lastSearch) {
            let expireSearch: Date = this.settingsService.loadSettings().calculateSearchExpires(lastSearch);
            if (nextSearchExpire && nextSearchExpire.getTime() < expireSearch.getTime()) expireSearch = nextSearchExpire;
            const expired = expireSearch.getTime() <= this.settings.calculateCurrentTime().getTime();

            if (expired) {
                Log.debug(Log.Tag.OfferService, "Search results expired.");
            }

            return expired;
        } else {
            return false;
        }
    }

    searchComplete(offerId: number): boolean {
        const expired = this.searchExpired();
        const network = this.networkService.connectionType !== NetworkConnectionType.None;

        const offerList: Array<number> = this.storageService.loadOffersListSearch();
        // only expire the search if there is network connectivity so a new search can be run
        if (offerList && !(expired && network) ? true : false) {
            if (!offerId) return true;
            // if we're verifying that a specific offer is in the list, then we need to check before saying the search result is complete
            return offerList.filter(id => id === offerId).length > 0;
        } else {
            return false;
        }
    }

    haveOffers(): boolean {
        const offerList: Array<number> = this.storageService.loadOffersListSearch();
        return offerList && offerList.length > 0;
    }

    loadOffers(includeRedeemed: boolean, filter: OfferFilter = {}, offerSet: "free"|"discount"|"all"|"favorites" = "free"): Array<OfferInfo> {
        if (offerSet === "all" && this.settingsService.loadSettings().interleavedCoupons) {
            const free = this.loadOfferList(true, false, filter, "free");
            const discount = this.loadOfferList(true, false, filter, "discount");
            const all = [];
            let i = 0;
            let j = 0;
            while (i < free.length || j < discount.length) {
                if ((i <= j || j >= discount.length) && i < free.length) {
                    all.push(free[i]);
                    i++;
                } else if (j < discount.length) {
                    all.push(discount[j]);
                    j++
                }
            }
            return all;
        } else {
            return this.loadOfferList(true, false, filter, offerSet);
        }
    }

    loadOfferImageURLs(offers: Array<OfferInfo>): Array<{url: string, hash: string}> {
        const urls = [];
        if (offers) {
            offers.forEach((offerInfo: OfferInfo) => {
                if (offerInfo.imageUrl) {
                    urls.push({url: offerInfo.imageUrl, hash: offerInfo.imageHash});
                }
                if (offerInfo.whatElseDescription && offerInfo.whatElseDescription.imageUrl) {
                    urls.push({url: offerInfo.whatElseDescription.imageUrl, hash: offerInfo.whatElseDescription.imageHash});
                }
            });
        }
        return urls;
    }

    recordOfferExperience(offerId: number, thumbs: "up"|"down"|"skip"): SuccessResult {
        const result = this.service.recordOfferExperience(offerId, thumbs);
        if (result.success) {
            // this.notificationService.cancelNotification(offerId);
        }
        return result;
    }

    recordOfferAdditionalPurchase(offerId: number, yesNo: "yes"|"no"): SuccessResult {
        const result = this.service.recordOfferAdditionalPurchase(offerId, yesNo);
        return result;
    }

    performSearch(): Promise<Array<OfferInfo>> {
        this.storageService.storeOfferSearchDate(this.settings.calculateCurrentTime());
        return this.service.performSearch();
    }

    alternateLocations(search: OfferSearch = null, limit: number = 3): Promise<AlternateLocationsList> {
        return this.service.alternateLocations(search, limit);
    }

    lastSearchDate(): Date {
        return this.storageService.loadOfferSearchDate();
    }

    loadSponsor(): SponsorInfo {
        return this.storageService.loadSponsorInfo();
    }

    loadFilterOptions(): OfferFilterOptions {
        // if (true) return this.sampleService.loadFilterOptions();
        return this.service.loadFilterOptions();
    }

    private loadOfferList(includeRedeemed: boolean, includeDoNotShow: boolean, filter: OfferFilter = {}, offerSet: "free"|"discount"|"all"|"favorites" = "free"): Array<OfferInfo> {
        // TODO sometimes the this.storageService.loadOffersListSearch() gets broken and won't load everything. Calling resetOffers once will fix it.
        // I need a better understanding of what is happening. But at the very least, we should have some sort of manual reset functionality in the app.
        // this.storageService.resetOffers();
        try {
            const settings = this.settings;
            const includeFree = offerSet === "all" || offerSet === "free";
            const includeDiscount = offerSet === "all" || offerSet === "discount";
            const includeFavorites = offerSet === "all" || offerSet === "favorites";
            const excludeFavorites = !includeFavorites && settings.gamificationFavoriteMode;
            const currentTime = settings.calculateCurrentTime();
            const retainRedeemedForMillis = 1000 * 60 * 60 * 24 * 7;
            const retainExpiredForMillis = 1000 * 60 * 60 * 24 * 1;

            const expiredOffers: Array<OfferInfo> = [];

            let loadedOffers: Array<OfferInfo> = null;
            const offerList: Array<number> = this.storageService.loadOffersListSearch();
            if (offerList || includeRedeemed) {
                loadedOffers = [];
            }
            const includeOffer = (info: OfferInfo): boolean => {
                if (info && (!excludeFavorites || !info.favorite) && ((includeFree && info.free) || (includeFavorites && info.favorite) || (includeDiscount && !info.free && (settings.alwaysShowBouncebacks || !info.bounceback)))) {
                    if (filter) {
                        if (filter.freeForm && info.filterText) {
                            const searchText = filter.freeForm.toLowerCase();
                            // exclude offer if the search text isn't found anywhere
                            if (info.filterText.indexOf(searchText) < 0) return false;
                        }
                        if (filter.excludeOnline && info.onlineOnly) return false;
                        if (filter.excludeLocal && !info.onlineOnly) return false;
                        if (filter.categories && filter.categories.length > 0) {
                            // exclude offer if category filters are used and there are not categories on the offer
                            if (!info.category || info.category.length === 0) return false;
                            let found = false;
                            for (const category of info.category) {
                                if (filter.categories.indexOf(category) >= 0) found = true;
                            }
                            // exclude offer if it has NONE of the required categories
                            if (!found) return false;
                        }
                        if (filter.tags && filter.tags.length > 0) {
                            // exclude offer if tag filters are used and there are not tags on the offer
                            if (!info.tags || info.tags.length === 0) return false;
                            let found = false;
                            for (const tag of info.tags) {
                                if (filter.tags.indexOf(tag.tag) >= 0) found = true;
                            }
                            // exclude offer if it has NONE of the required tags
                            if (!found) return false;
                        }
                    }
                    return true;
                } else {
                    return false;
                }
            };

            if (offerList) {
                // returned cached result instead of querying
                offerList.forEach((id) => {
                    const info = this.storageService.loadOfferInfo(id);
                    const include = includeOffer(info);
                    if (include) {
                        let expireMillis = !info.expirationDate ? currentTime.getTime() + 1 : toDate(info.expirationDate).getTime();
                        if (expireMillis > currentTime.getTime() && info.redeemedDate) {
                            // if it's already redeemed, then we'll use the bounceback expiration as the offer's expiration
                            const bouncebackOffer = info.bounceBackOfferId ? this.storageService.loadOfferInfo(info.bounceBackOfferId) : null;
                            if (bouncebackOffer) {
                                const bouncebackRetainMillis = !bouncebackOffer.expirationDate ? currentTime.getTime() + 1 : toDate(bouncebackOffer.expirationDate).getTime() + retainExpiredForMillis;
                                if (bouncebackRetainMillis > expireMillis) {
                                    // if the expiration based on the bounceback is longer than the calculated expiration, use it instead
                                    expireMillis = bouncebackRetainMillis;
                                }
                            }
                        }
                        if (expireMillis > currentTime.getTime()) {
                            loadedOffers.push(info);
                        } else {
                            expiredOffers.push(info);
                        }
                    }
                });
            }

            if (includeRedeemed) {
                // need to find any recently redeemed offers that are NOT in the current search results and add those
                // offers at the end of the list so that the user can still see them, and so that if any bounceback notifications
                // occur, we can still take the user to those bounceback offers
                const redeemedOfferList: Array<number> = this.storageService.loadOffersListRedeemed();
                redeemedOfferList.forEach((id) => {
                    const info = this.storageService.loadOfferInfo(id);
                    if (!info.redeemedDate) return;

                    const bouncebackOffer = info.bounceBackOfferId ? this.storageService.loadOfferInfo(info.bounceBackOfferId) : null;
                    let expireMillis = !info.expirationDate ? currentTime.getTime() + 1 : toDate(info.expirationDate).getTime() + retainExpiredForMillis;

                    const expireRedeemedMillis = toDate(info.redeemedDate).getTime() + retainRedeemedForMillis;
                    if (expireRedeemedMillis > expireMillis) {
                        // if the expiration based on the redemption is longer than the default, use it
                        expireMillis = expireRedeemedMillis;
                    }
                    if (bouncebackOffer) {
                        const bouncebackRetainMillis = !bouncebackOffer.expirationDate ? currentTime.getTime() + 1 : toDate(bouncebackOffer.expirationDate).getTime() + retainExpiredForMillis;
                        if (bouncebackRetainMillis > expireMillis) {
                            // if the expiration based on the bounceback is longer than the calculated expiration, use it instead
                            expireMillis = bouncebackRetainMillis;
                        }
                    }

                    if (expireMillis > currentTime.getTime()) {
                        // the offer was redeemed within the past week, so continue to show it
                        if (includeOffer(info)) {
                            if (includeFree && info.free) loadedOffers.push(info);
                            if (includeDiscount && !info.free) loadedOffers.push(info);
                        }
                        // now we need to attach the bounceback as well if it exists
                        if (includeDiscount && bouncebackOffer) {
                            if (!excludeFavorites || !bouncebackOffer.favorite) loadedOffers.push(bouncebackOffer);
                        }
                    }
                });

                expiredOffers.forEach((info) => {
                    const expireMillis = !info.expirationDate ? currentTime.getTime() : toDate(info.expirationDate).getTime() + retainExpiredForMillis;
                    if (expireMillis > currentTime.getTime()) {
                        // the offer was redeemed within the past week, so continue to show it
                        loadedOffers.push(info);
                    }
                });
            }

            if (loadedOffers) {
                const doNotShow: {[key: string]: boolean } = {};
                loadedOffers.forEach((offer: OfferInfo) => {
                    if (offer.bounceBackOfferId) {
                        if (!offer.thumbsDate && !includeDoNotShow) {
                            doNotShow[offer.bounceBackOfferId] = true;
                        }
                    }
                });
                const offers: Array<OfferInfo> = [];
                const addOffer = (offer: OfferInfo): boolean => {
                    if (offer) {
                        const noShow = doNotShow[offer.id];
                        if (!noShow) {
                            offers.push(offer);
                            if (offer.bounceBackOfferId) {
                                loadedOffers.forEach((findOffer) => {
                                    if (findOffer.id === offer.bounceBackOfferId) {
                                        addOffer(findOffer);
                                    }
                                });
                            }
                            doNotShow[offer.id] = true;
                            return true;
                        }
                    }
                    return false;
                };
                // returned cached result instead of querying
                loadedOffers.forEach(addOffer);
                const now = settings.calculateCurrentTime();
                if (settings.sortOffersLocal) {
                    let location: LocationInfo = null;
                    const resultLocation = this.storageService.loadOfferSearchResultLocation();
                    if (resultLocation && resultLocation.longitude && resultLocation.latitude) {
                        location = { longitude: resultLocation.longitude, latitude: resultLocation.latitude };
                    }

                    const relativeOfferInfo = (info: OfferInfo): void => {
                        const infoRelative: any = info as any;
                        infoRelative.relativeIsNew = OfferItemView.isOfferNew(now, info) || OfferItemView.isOfferUnseenLately(now, info) ? 1 : 0;
                        infoRelative.relativeDistance = OfferItemView.calculateNearestDistanceInMiles(info, location) - info.sortPriority + (info.onlineOnly ? settings.sortOnlinePriorityShift : 0);
                        infoRelative.relativeIsSample = !!info.sample ? 1 : 0;
                        infoRelative.relativeRemainingDays = OfferItemView.calculateDaysRemaining(now, info) > 0 ? 1 : 0;
                        infoRelative.relativeRedeemed = OfferItemView.isOfferRedeemed(info, now) ? 1 : 0;
                        infoRelative.relativeRedeemedTime = infoRelative.relativeRedeemed ? toDate(info.redeemedDate).getTime() : 0;
                        infoRelative.relativeAvailable = info.nextAvailableDate ? 1 : 0;
                        infoRelative.relativeAvailableTime = infoRelative.relativeAvailable ? toDate(info.nextAvailableDate).getTime() : 0;
                    };
                    offers.forEach(relativeOfferInfo);
                    offers.sort((o1, o2) => {
                        const new1 = (o1 as any).relativeIsNew;
                        const new2 = (o2 as any).relativeIsNew;
                        const distance1 = (o1 as any).relativeDistance;
                        const distance2 = (o2 as any).relativeDistance;
                        const sample1 = (o1 as any).relativeIsSample;
                        const sample2 = (o2 as any).relativeIsSample;
                        const remaining1 = (o1 as any).relativeRemainingDays;
                        const remaining2 = (o2 as any).relativeRemainingDays;
                        const redeemed1 = (o1 as any).relativeRedeemed;
                        const redeemed2 = (o2 as any).relativeRedeemed;
                        const available1 = (o1 as any).relativeAvailable;
                        const available2 = (o2 as any).relativeAvailable;
                        // new (distance priority) -> available (distance priority) -> sample (distance priority) -> next available -> redeemed -> expired

                        if (remaining1 === remaining2) { // expired check
                            if (remaining1) {
                                // not expired, so check redemption
                                if (redeemed1 === redeemed2) {
                                    if (redeemed1) {
                                        // redeemed, so sort by next available
                                        if (available1 === available2) {
                                            if (available1) {
                                                return (o1 as any).relativeAvailableTime - (o2 as any).relativeAvailableTime;
                                            } else {
                                                return (o1 as any).relativeRedeemedTime - (o2 as any).relativeRedeemedTime;
                                            }
                                        } else {
                                            return -(available1 - available2);
                                        }
                                    } else {
                                        // not redeemed, so sort by samples and new
                                        if (sample1 === sample2) {
                                            if (!sample1) {
                                                // not a sample, so sort by newness
                                                if (new1 === new2) {
                                                    // sort new by distance priority
                                                    return distance1 - distance2;
                                                } else {
                                                    // sort by new or not
                                                    return -(new1 - new2);
                                                }
                                            } else {
                                                // is a sample so sort by distance
                                                return distance1 - distance2;
                                            }
                                        } else {
                                            // sort by whether sample or not
                                            return sample1 - sample2;
                                        }
                                    }
                                } else {
                                    // unredeemed should be first
                                    return redeemed1 - redeemed2;
                                }
                            } else {
                                // expired, so sort by expiration
                                return -(toDate(o1.expirationDate).getTime() - toDate(o2.expirationDate).getTime());
                            }
                        } else {
                            // not expired should be first
                            return remaining1 - remaining2;
                        }
                    });
                }
                return offers;
            } else {
                return null;
            }
        } catch (error) {
            // this *shouldn't* happen, but if it does, then there might be some kind of data corruption, so just totally reset...
            Log.error(Log.Tag.OfferService, error);
            this.storageService.resetOffers();
            return null;
        }
    }
}
